import type { StudioApi } from '../useTechnicalStudio';
import { PIVOT_METHODS, TIMEFRAMES, SYMBOLS, PIVOT_USE_CASES } from '@/mocks/technical';
import { fmtPrice, NumberField, Segmented, SectionHeading } from './ui';

function pricePosition(current: number, levels: { label: string; price: number }[], pivot: number): string {
  const sorted = [...levels].sort((a, b) => b.price - a.price);
  if (current >= sorted[0].price) return `Trên ${sorted[0].label}`;
  for (let i = 0; i < sorted.length - 1; i++) {
    const upper = sorted[i];
    const lower = sorted[i + 1];
    if (current <= upper.price && current >= lower.price) {
      if (current >= pivot) return `Giữa Pivot và ${upper.label}`;
      return `Giữa ${lower.label} và Pivot`;
    }
  }
  return `Dưới ${sorted[sorted.length - 1].label}`;
}

export default function PivotSection({ api }: { api: StudioApi }) {
  const { pivot, setPivot, pivotLevels, mtf, pip, decimals, changeSymbol } = api;
  const current = pivot.current;
  const { high, low, close, open } = pivot.inputs[pivot.timeframe];

  const setInput = (key: 'high' | 'low' | 'close' | 'open', v: number) =>
    setPivot({
      ...pivot,
      inputs: {
        ...pivot.inputs,
        [pivot.timeframe]: { ...pivot.inputs[pivot.timeframe], [key]: v },
      },
    });

  const invalid = low > high;

  const ladderLevels = [
    ...pivotLevels.levels.map((l) => ({ key: l.key, label: l.label, price: l.price, isPivot: false })),
    { key: 'pivot', label: 'Pivot', price: pivotLevels.pivot, isPivot: true },
  ];

  const position = pricePosition(current, pivotLevels.levels, pivotLevels.pivot);
  const distToPivot = (current - pivotLevels.pivot) / pip;
  const nearestR = [...pivotLevels.levels].filter((l) => l.price >= current).sort((a, b) => a.price - b.price)[0];

  return (
    <section id="pivot" className="scroll-mt-24">
      <SectionHeading
        eyebrow="Công cụ 1"
        title="Máy tính Pivot Point"
        subtitle="Tính toán các mức Pivot và vùng hỗ trợ, kháng cự tham chiếu từ dữ liệu giá."
      />

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Input panel */}
        <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-base font-bold text-slate-900">Dữ liệu thị trường</h3>
          </div>

          <div className="mt-5">
            <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Cặp tiền tệ</span>
            <div className="flex flex-wrap gap-2">
              {SYMBOLS.map((s) => {
                const active = s.value === pivot.symbol;
                return (
                  <button
                    key={s.value}
                    type="button"
                    onClick={() => changeSymbol(s.value)}
                    className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                      active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
          </div>

          {invalid && (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs font-medium text-rose-600">
              <i className="ri-error-warning-line text-sm" />
              Giá cao nhất (High) đang nhỏ hơn giá thấp nhất (Low). Vui lòng kiểm tra lại.
            </div>
          )}

          <div className="mt-5 space-y-4">
            <NumberField label="Giá cao nhất" hint="High" value={high} onChange={(v) => setInput('high', v)} />
            <NumberField label="Giá thấp nhất" hint="Low" value={low} onChange={(v) => setInput('low', v)} />
            <NumberField label="Giá đóng cửa" hint="Close" value={close} onChange={(v) => setInput('close', v)} />
            <NumberField label="Giá mở cửa" hint="Open" value={open} onChange={(v) => setInput('open', v)} />
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Khung thời gian</span>
              <Segmented
                options={TIMEFRAMES}
                value={pivot.timeframe}
                onChange={(v) => setPivot({ ...pivot, timeframe: v })}
              />
            </div>
            <div>
              <span className="mb-2 block text-xs font-semibold uppercase tracking-wide text-slate-500">Phương pháp Pivot</span>
              <div className="flex flex-wrap gap-2">
                {PIVOT_METHODS.map((m) => {
                  const active = m.value === pivot.method;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setPivot({ ...pivot, method: m.value })}
                      className={`whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
                        active ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                      }`}
                    >
                      {m.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Current price position */}
          <div className="mt-6 rounded-2xl border border-slate-200/80 bg-slate-50/60 p-4">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Giá hiện tại</span>
              <input
                type="number"
                step={pip}
                value={current}
                onChange={(e) => setPivot({ ...pivot, current: parseFloat(e.target.value) || 0 })}
                className="w-28 rounded-lg border border-slate-200 bg-white px-2 py-1 text-right font-mono text-sm font-bold text-slate-800 outline-none focus:border-violet-400"
              />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3">
              <div className="rounded-lg bg-white p-3">
                <p className="text-[10px] font-medium uppercase text-slate-400">Vị trí giá</p>
                <p className="mt-1 text-xs font-semibold text-slate-700">{position}</p>
              </div>
              <div className="rounded-lg bg-white p-3">
                <p className="text-[10px] font-medium uppercase text-slate-400">Đến Pivot</p>
                <p className={`mt-1 font-mono text-sm font-bold ${distToPivot >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                  {distToPivot >= 0 ? '+' : ''}
                  {distToPivot.toFixed(0)} pip
                </p>
              </div>
              <div className="rounded-lg bg-white p-3">
                <p className="text-[10px] font-medium uppercase text-slate-400">Đến {nearestR?.label ?? 'R1'}</p>
                <p className="mt-1 font-mono text-sm font-bold text-rose-600">
                  {nearestR ? ((nearestR.price - current) / pip).toFixed(0) : '—'} pip
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Level map + confluence */}
        <div className="flex flex-col gap-6">
          <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-base font-bold text-slate-900">Pivot Level Map</h3>
              <span className="rounded-full bg-slate-100 px-2.5 py-0.5 font-mono text-[11px] font-semibold text-slate-500">
                {pivot.method.toUpperCase()}
              </span>
            </div>
            <p className="mt-1 text-xs text-slate-400">Thang giá tham chiếu — Pivot nổi bật ở giữa.</p>
            <div className="mt-4">
              <PriceLadderView levels={ladderLevels} current={current} pip={pip} decimals={decimals} />
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
            <h3 className="font-display text-base font-bold text-slate-900">Level Confluence</h3>
            <p className="mt-1 text-xs text-slate-400">Mức giá trùng nhau giữa nhiều khung thời gian (≤ 5 pip).</p>
            <div className="mt-4 space-y-3">
              {mtfConfluence(mtf, pip).length === 0 && (
                <p className="rounded-xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-400">
                  Không có mức giá trùng nhau trong khoảng 5 pip.
                </p>
              )}
              {mtfConfluence(mtf, pip).map((c, i) => (
                <div key={i} className="flex items-center justify-between rounded-xl border border-slate-200/80 bg-slate-50/60 px-4 py-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800">
                      {c.a.timeframe} {c.a.level} <span className="font-mono text-violet-600">{fmtPrice(c.a.price, decimals)}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {c.b.timeframe} {c.b.level} <span className="font-mono">{fmtPrice(c.b.price, decimals)}</span>
                    </p>
                  </div>
                  <span className="rounded-full bg-violet-50 px-2.5 py-1 font-mono text-xs font-semibold text-violet-700">
                    {c.distance.toFixed(0)} pip
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl border border-slate-200/80 bg-white p-6">
            <h3 className="font-display text-base font-bold text-slate-900">Pivot theo nhiều khung thời gian</h3>
            <p className="mt-1 text-xs text-slate-400">Ma trận các mức Pivot Daily, Weekly và Monthly.</p>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[420px] text-left">
                <thead>
                  <tr className="border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-3 font-semibold">Khung</th>
                    <th className="py-2 pr-3 font-semibold">Pivot</th>
                    <th className="py-2 pr-3 font-semibold">R1</th>
                    <th className="py-2 pr-3 font-semibold">R2</th>
                    <th className="py-2 pr-3 font-semibold">S1</th>
                    <th className="py-2 font-semibold">S2</th>
                  </tr>
                </thead>
                <tbody>
                  {mtf.map((m) => {
                    const r1 = m.levels.find((l) => l.key === 'r1')?.price ?? 0;
                    const r2 = m.levels.find((l) => l.key === 'r2')?.price ?? 0;
                    const s1 = m.levels.find((l) => l.key === 's1')?.price ?? 0;
                    const s2 = m.levels.find((l) => l.key === 's2')?.price ?? 0;
                    return (
                      <tr key={m.timeframe} className="border-b border-slate-100 font-mono text-xs">
                        <td className="py-2.5 pr-3 font-sans text-sm font-semibold text-slate-700">{m.timeframe}</td>
                        <td className="py-2.5 pr-3 font-bold text-slate-900">{fmtPrice(m.pivot, decimals)}</td>
                        <td className="py-2.5 pr-3 text-emerald-600">{fmtPrice(r1, decimals)}</td>
                        <td className="py-2.5 pr-3 text-emerald-600">{fmtPrice(r2, decimals)}</td>
                        <td className="py-2.5 pr-3 text-rose-600">{fmtPrice(s1, decimals)}</td>
                        <td className="py-2.5 text-rose-600">{fmtPrice(s2, decimals)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Use cases */}
      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        {PIVOT_USE_CASES.map((u) => (
          <div key={u.title} className="rounded-2xl border border-slate-200/80 bg-slate-50/50 p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <i className={u.icon} />
            </span>
            <h4 className="mt-3 text-sm font-bold text-slate-800">{u.title}</h4>
            <p className="mt-1 text-xs leading-relaxed text-slate-500">{u.desc}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function PriceLadderView({
  levels,
  current,
  pip,
  decimals,
}: {
  levels: { key: string; label: string; price: number; isPivot?: boolean }[];
  current: number;
  pip: number;
  decimals: number;
}) {
  const rows = [...levels].sort((a, b) => b.price - a.price);
  return (
    <div className="space-y-1.5">
      {rows.map((l) => {
        const distPips = (l.price - current) / pip;
        return (
          <div
            key={l.key}
            className={`flex items-center gap-3 rounded-lg px-3 py-2 ${l.isPivot ? 'bg-slate-900 text-white' : 'bg-slate-50'}`}
          >
            <span className={`w-12 shrink-0 font-mono text-xs font-bold ${l.isPivot ? 'text-white' : 'text-slate-500'}`}>
              {l.label}
            </span>
            <span className={`shrink-0 font-mono text-sm font-bold tabular-nums ${l.isPivot ? 'text-white' : 'text-slate-800'}`}>
              {fmtPrice(l.price, decimals)}
            </span>
            <span
              className={`ml-auto shrink-0 font-mono text-[11px] tabular-nums ${
                distPips > 0 ? 'text-emerald-600' : distPips < 0 ? 'text-rose-600' : 'text-slate-400'
              }`}
            >
              {distPips > 0 ? '+' : ''}
              {distPips.toFixed(0)} pip
            </span>
          </div>
        );
      })}
    </div>
  );
}

function mtfConfluence(
  mtf: { timeframe: string; pivot: number; levels: { key: string; label: string; price: number }[] }[],
  pip: number,
) {
  const hits: { a: { timeframe: string; level: string; price: number }; b: { timeframe: string; level: string; price: number }; distance: number }[] = [];
  for (let i = 0; i < mtf.length; i++) {
    for (let j = i + 1; j < mtf.length; j++) {
      const aLevels = [...mtf[i].levels, { key: 'pivot', label: 'Pivot', price: mtf[i].pivot }];
      const bLevels = [...mtf[j].levels, { key: 'pivot', label: 'Pivot', price: mtf[j].pivot }];
      for (const al of aLevels) {
        for (const bl of bLevels) {
          const dist = Math.abs(al.price - bl.price) / pip;
          if (dist <= 5) {
            hits.push({
              a: { timeframe: mtf[i].timeframe, level: al.label, price: al.price },
              b: { timeframe: mtf[j].timeframe, level: bl.label, price: bl.price },
              distance: dist,
            });
          }
        }
      }
    }
  }
  return hits.sort((a, b) => a.distance - b.distance).slice(0, 4);
}